import React, { useState, useEffect } from 'react';
import { FcGoogle } from "react-icons/fc";
import { BsX } from "react-icons/bs";

// TypewriterText component for animated text display
export const TypewriterText: React.FC<{ 
  text: string; 
  delay?: number; 
  className?: string;
  onComplete?: () => void;
  skipAnimation?: boolean;
}> = ({ text, delay = 30, className, onComplete, skipAnimation = false }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    // Reset when text changes
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
  }, [text]);
  
  useEffect(() => {
    if (skipAnimation) {
      setDisplayedText(text);
      setCurrentIndex(text.length);
      setIsComplete(true);
      if (onComplete) {
        onComplete();
      }
      return;
    }

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay);
      
      return () => clearTimeout(timer);
    } else if (!isComplete) {
      setIsComplete(true);
      // Call the onComplete callback when the text is finished typing
      if (onComplete) {
        onComplete();
      }
    }
  }, [currentIndex, delay, text, isComplete, onComplete, skipAnimation]);
  
  // Only show cursor when still typing (not complete)
  return (
    <p className={className}>
      {displayedText}
      {!isComplete && <span className="typewriter-cursor"></span>}
    </p>
  );
};

export interface TypewriterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrimaryAction: (inputValue?: string) => void;
  title: string;
  messages: string[];
  primaryButtonText: string;
  primaryButtonIcon?: React.ReactNode;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  onSecondaryAction?: () => void;
  secondaryButtonDisabled?: boolean;
  className?: string;
  showTextInput?: boolean;
  textInputPlaceholder?: string;
  aiResponseText?: string;
  isLoadingAiResponse?: boolean;
}

const TypewriterModal: React.FC<TypewriterModalProps> = ({
  isOpen,
  onClose,
  onPrimaryAction,
  title,
  messages,
  primaryButtonText,
  primaryButtonIcon,
  primaryButtonDisabled = false,
  secondaryButtonText,
  onSecondaryAction,
  secondaryButtonDisabled = false,
  className,
  showTextInput = false,
  textInputPlaceholder = "Enter your response...",
  aiResponseText,
  isLoadingAiResponse = false
}) => {
  // Animation states for each message
  const [visibleMessages, setVisibleMessages] = useState<boolean[]>([]);
  const [showButtons, setShowButtons] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showAiResponse, setShowAiResponse] = useState(false);
  
  // Sequence animation when the modal opens
  useEffect(() => {
    if (isOpen) {
      // Start with just the first message visible
      const initialState = [true];
      for (let i = 1; i < messages.length; i++) {
        initialState.push(false);
      }
      setVisibleMessages(initialState);
      setShowButtons(false);
      setSkipAnimation(false);
      setInputValue('');
      setShowAiResponse(false);
    } else {
      // Reset states when modal closes
      setVisibleMessages([]);
      setShowButtons(false);
      setSkipAnimation(false);
      setInputValue('');
      setShowAiResponse(false);
    }
  }, [isOpen, messages.length]);

  // Show AI response after it's loaded
  useEffect(() => {
    if (aiResponseText && !isLoadingAiResponse) {
      setShowAiResponse(true);
    }
  }, [aiResponseText, isLoadingAiResponse]);
  
  // Function to handle when a message is complete
  const handleMessageComplete = (index: number) => {
    if (index < messages.length - 1) {
      // Show next message
      setVisibleMessages(prev => {
        const newState = [...prev];
        newState[index + 1] = true;
        return newState;
      });
    } else {
      // Last message complete, show buttons
      setShowButtons(true);
    }
  };

  // Function to handle click to skip animation
  const handleSkipAnimation = () => {
    setSkipAnimation(true);
    // Show all messages
    const allVisible = new Array(messages.length).fill(true);
    setVisibleMessages(allVisible);
    // Show buttons
    setShowButtons(true);
  };

  // Function to handle primary action with input value
  const handlePrimaryAction = () => {
    onPrimaryAction(showTextInput ? inputValue : undefined);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={`auth-modal ${className || ''}`} onClick={handleSkipAnimation}>
      <div className="login-info-content">
        <h3>{title}</h3>
        
        {messages.map((message, index) => (
          visibleMessages[index] && (
            <TypewriterText 
              key={index}
              text={message}
              delay={30}
              onComplete={() => handleMessageComplete(index)}
              skipAnimation={skipAnimation}
            />
          )
        ))}
        
        {showButtons && showTextInput && (
          <div className="text-input-container fade-in">
            <input
              type="text"
              className="project-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={textInputPlaceholder}
              onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to modal
            />
          </div>
        )}

        {showAiResponse && (
          <div className="ai-response fade-in">
            <TypewriterText 
              text={aiResponseText || ""}
              delay={20}
              className="ai-response-text"
              skipAnimation={skipAnimation}
            />
          </div>
        )}

        {isLoadingAiResponse && (
          <div className="loading-spinner-container fade-in">
            <div className="loading-spinner"></div>
            <p>Thinking about your project...</p>
          </div>
        )}
        
        {showButtons && !isLoadingAiResponse && (
          <div className="login-info-buttons fade-in">
            {secondaryButtonText && (
              <button 
                className="login-info-cancel" 
                onClick={onSecondaryAction || onClose}
                disabled={secondaryButtonDisabled}
              >
                {secondaryButtonText}
              </button>
            )}
            <button 
              className="login-info-continue" 
              onClick={handlePrimaryAction}
              disabled={primaryButtonDisabled || (showTextInput && !inputValue.trim())}
            >
              {primaryButtonIcon} {primaryButtonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypewriterModal; 